import { Box, Chip, TextField, Select, TableContainer, Table, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

// ── Hub ──────────────────────────────────────────────────────────────────────

export const PageContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(4),
	backgroundColor: theme.palette.background.default,
	minHeight: 'calc(100vh - 120px)',
}));

export const HubGrid = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
	gap: theme.spacing(3),
	[theme.breakpoints.down('sm')]: {
		gridTemplateColumns: '1fr',
	},
}));

export const HubEmptyState = styled(Box)(({ theme }) => ({
	textAlign: 'center',
	marginTop: theme.spacing(10),
	padding: theme.spacing(4),
}));

// ── Detail header ─────────────────────────────────────────────────────────────

export const DetailHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: theme.spacing(3),
	padding: theme.spacing(2.5, 3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: theme.spacing(1.5),
	},
}));

export const DetailHeaderLeft = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}));

export const DocenteChip = styled(Chip)(({ theme }) => ({
	fontSize: '0.75rem',
	fontWeight: 500,
	backgroundColor: theme.palette.grey[100],
	color: theme.palette.text.secondary,
}));

// ── Filters ──────────────────────────────────────────────────────────────────

export const FiltersContainer = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr auto',
	gap: theme.spacing(2),
	marginBottom: theme.spacing(3),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	alignItems: 'center',
	[theme.breakpoints.down('sm')]: {
		gridTemplateColumns: '1fr',
	},
}));

export const SingleFilterContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	gap: theme.spacing(2),
	marginBottom: theme.spacing(3),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}));

export const SearchField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor: theme.palette.background.default,
	},
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
	minWidth: 200,
	backgroundColor: theme.palette.background.default,
	[theme.breakpoints.down('sm')]: {
		minWidth: '100%',
	},
}));

// ── Results header ────────────────────────────────────────────────────────────

export const ResultsHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: theme.spacing(3),
}));

// ── Table ─────────────────────────────────────────────────────────────────────

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}));

export const StyledTable = styled(Table)(() => ({
	minWidth: 600,
}));

export const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
	padding: theme.spacing(1.5, 2),
	fontWeight: 600,
	backgroundColor: theme.palette.grey[50],
	borderBottom: `2px solid ${theme.palette.divider}`,
	color: theme.palette.text.secondary,
	fontSize: '0.8rem',
	textTransform: 'uppercase',
	letterSpacing: '0.05em',
	whiteSpace: 'nowrap',
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	padding: theme.spacing(1.75, 2),
	borderBottom: `1px solid ${theme.palette.divider}`,
	verticalAlign: 'middle',
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:hover': {
		backgroundColor: theme.palette.action.hover,
	},
	'&:last-child td': {
		borderBottom: 0,
	},
}));

export const FileNameCell = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}));

const getFileIconColors = (resourcetype: string, theme: any) => {
	switch (resourcetype) {
		case 'pdf':   return { bg: '#FDECEA', color: '#C62828' };
		case 'docx':  return { bg: '#E3F2FD', color: '#1565C0' };
		case 'pptx':  return { bg: '#FFF3E0', color: '#E65100' };
		case 'xlsx':  return { bg: '#E8F5E9', color: '#2E7D32' };
		case 'video': return { bg: '#F3E5F5', color: '#6A1B9A' };
		case 'imagen':return { bg: '#E0F7FA', color: '#00697A' };
		default:      return { bg: theme.palette.grey[100], color: theme.palette.grey[600] };
	}
};

export const FileIconContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'resourcetype',
})<{ resourcetype: string }>(({ theme, resourcetype }) => {
	const { bg, color } = getFileIconColors(resourcetype, theme);
	return {
		width: 36,
		height: 36,
		borderRadius: theme.shape.borderRadius,
		backgroundColor: bg,
		color,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		fontSize: '0.65rem',
		fontWeight: 700,
		letterSpacing: '0.03em',
	};
});

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
	textAlign: 'center',
	marginTop: theme.spacing(8),
	padding: theme.spacing(4),
}));

// ── Recursos card grid ────────────────────────────────────────────────────────

export const RecursosGrid = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
	gap: theme.spacing(3),
	[theme.breakpoints.down('sm')]: {
		gridTemplateColumns: '1fr',
	},
}));
